// mds-dojo — dijit/_MenuBase — FAITHFUL port of references/dijit/_MenuBase.js.
// Abstract base class for Menu and MenuBar: item hover/select/click handling,
// submenu popup open/close timers, keyboard search integration. declare from the
// faithful runtime.
//
// mds ADAPTATIONS (behavior-preserving):
//  - dojo `on.selector(matches, mouse.enter/leave/a11yclick/"focusin")` delegation
//    is done via the mds onSelector*/shim (mds dojo/on has no delegation and mds
//    mouse.enter/leave don't bubble). See ./_onSelector.
//  - `on.once(...)` is replaced by a local once() helper (absent on mds dojo/on).
//  - unused array/dom/mouse/dojo-window imports dropped.
import declare from '../dojo/_base/declare-runtime'; // declare
import domAttr from '../dojo/dom-attr';
import domClass from '../dojo/dom-class'; // domClass.toggle
import lang from '../dojo/_base/lang'; // lang.hitch
import on from '../dojo/on';
import a11yclick from './a11yclick';
import registry from './registry';
import _Widget from './_Widget';
import _CssStateMixin from './_CssStateMixin';
import _KeyNavContainer from './_KeyNavContainer';
import _TemplatedMixin from './_TemplatedMixin';
import { onSelector, onSelectorEnter, onSelectorLeave } from './_onSelector';

// Faithful substitute for on.once (fire the listener once, then self-remove).
function once(target: any, type: any, listener: any): any {
  var h = on(target, type, function (this: any) {
    h.remove();
    return listener.apply(this, arguments);
  });
  return h;
}

export default declare('dijit._MenuBase', [_Widget, _TemplatedMixin, _KeyNavContainer, _CssStateMixin], {
  // summary:
  //		Abstract base class for Menu and MenuBar.

  // selected: dijit/MenuItem
  //		Currently selected (a.k.a. highlighted) MenuItem, or null if no MenuItem is selected.
  selected: null,
  _setSelectedAttr: function (item: any) {
    if (this.selected != item) {
      if (this.selected) {
        this.selected._setSelected(false);
        this._onChildDeselect(this.selected);
      }
      if (item) {
        item._setSelected(true);
      }
      this._set('selected', item);
    }
  },

  // activated: [readonly] Boolean
  //		This Menu has been clicked (mouse or via space/arrow key) or opened as a submenu,
  //		so mere mouseover will open submenus.
  activated: false,
  _setActivatedAttr: function (val: any) {
    domClass.toggle(this.domNode, 'dijitMenuActive', val);
    domClass.toggle(this.domNode, 'dijitMenuPassive', !val);
    this._set('activated', val);
  },

  // parentMenu: [readonly] Widget
  //		pointer to menu that displayed me
  parentMenu: null,

  // popupDelay: Integer
  //		After a menu has been activated (by clicking on it etc.), number of milliseconds before hovering
  //		(without clicking) another MenuItem causes that MenuItem's popup to automatically open.
  popupDelay: 500,

  // passivePopupDelay: Integer
  //		For a passive (unclicked) Menu, number of milliseconds before hovering (without clicking) will cause
  //		the popup to open.  Default is Infinity, meaning you need to click the menu to open it.
  passivePopupDelay: Infinity,

  // autoFocus: Boolean
  //		A toggle to control whether or not a Menu gets focused when opened as a drop down from a MenuBar
  //		or DropDownButton/ComboButton.
  autoFocus: false,

  childSelector: function (/*DOMNode*/ node: any) {
    // Selector (passed to on.selector()) used to identify MenuItem child widgets, but exclude inert children
    // like MenuSeparator.
    var widget = registry.byNode(node);
    return node.parentNode == this.containerNode && widget && widget.focus;
  },

  postCreate: function () {
    var self = this,
      matches = typeof this.childSelector == 'string' ? this.childSelector : lang.hitch(this, 'childSelector');
    this.own(
      onSelectorEnter(this.containerNode, matches, function (this: any) {
        self.onItemHover(registry.byNode(this));
      }),
      onSelectorLeave(this.containerNode, matches, function (this: any) {
        self.onItemUnhover(registry.byNode(this));
      }),
      onSelector(this.containerNode, matches, a11yclick, function (this: any, evt: any) {
        self.onItemClick(registry.byNode(this), evt);
        evt.stopPropagation();
      }),
      onSelector(this.containerNode, matches, 'focusin', function (this: any) {
        self._onItemFocus(registry.byNode(this));
      }),
    );
    this.inherited(arguments);
  },

  onKeyboardSearch: function (/*MenuItem*/ item: any, /*Event*/ evt: any, /*String*/ searchString?: any, /*Number*/ numMatches?: any) {
    // Attach point for notification about when a menu item has been searched for
    // via the keyboard search mechanism.
    this.inherited(arguments);
    if (!!item && (numMatches == -1 || (!!item.popup && numMatches == 1))) {
      this.onItemClick(item, evt);
    }
  },

  _keyboardSearchCompare: function (/*dijit/_WidgetBase*/ item: any, /*String*/ searchString: any) {
    // Compares the searchString to the widget's text label.
    if (!!item.shortcutKey) {
      // accessKey matches have priority
      return searchString == item.shortcutKey.toLowerCase() ? -1 : 0;
    }
    return this.inherited(arguments) ? 1 : 0; // change return value of -1 to 1 so that searching continues
  },

  onExecute: function () {
    // Attach point for notification about when a menu item has been executed.
    // Used internally for Menus to signal to their parent to close them.
  },

  onCancel: function (/*Boolean*/ /*===== closeAll =====*/) {
    // Attach point for notification about when the user cancels the current menu.
  },

  _moveToPopup: function (/*Event*/ evt: any) {
    // This handles the right arrow key (left arrow key on RTL systems), which will
    // either open a submenu, or move to the next item in the ancestor MenuBar.

    if (this.focusedChild && this.focusedChild.popup && !this.focusedChild.disabled) {
      this.onItemClick(this.focusedChild, evt);
    } else {
      var topMenu = this._getTopMenu();
      if (topMenu && topMenu._isMenuBar) {
        topMenu.focusNext();
      }
    }
  },

  _onPopupHover: function (/*Event*/ /*===== evt =====*/) {
    // This handler is called when the mouse moves over the popup.

    // highlight the parent menu item pointing to this popup (in case user temporarily moused over another MenuItem)
    this.set('selected', this.currentPopupItem);

    // cancel the pending close (if there is one)
    this._stopPendingCloseTimer();
  },

  onItemHover: function (/*MenuItem*/ item: any) {
    // Called when cursor is over a MenuItem.

    // Don't do anything unless user has "activated" the menu.
    if (this.activated) {
      this.set('selected', item);
      if (item.popup && !item.disabled && !this.hover_timer) {
        this.hover_timer = this.defer(function (this: any) {
          this._openItemPopup(item);
        }, this.popupDelay);
      }
    } else if (this.passivePopupDelay < Infinity) {
      if (this.passive_hover_timer) {
        this.passive_hover_timer.remove();
      }
      this.passive_hover_timer = this.defer(function (this: any) {
        this.onItemClick(item, { type: 'click' });
      }, this.passivePopupDelay);
    }

    this._hoveredChild = item;

    item._set('hovering', true);
  },

  _onChildDeselect: function (item: any) {
    // Called when a child MenuItem becomes deselected.   Setup timer to close its popup.

    this._stopPopupTimer();

    // Setup timer to close all popups that are open and descendants of this menu.
    if (this.currentPopupItem == item) {
      this._stopPendingCloseTimer();
      this._pendingClose_timer = this.defer(function (this: any) {
        this._pendingClose_timer = null;
        this.currentPopupItem = null;
        item._closePopup(); // this calls onClose
      }, this.popupDelay);
    }
  },

  onItemUnhover: function (/*MenuItem*/ item: any) {
    // Callback fires when mouse exits a MenuItem

    if (this._hoveredChild == item) {
      this._hoveredChild = null;
    }

    if (this.passive_hover_timer) {
      this.passive_hover_timer.remove();
      this.passive_hover_timer = null;
    }

    item._set('hovering', false);
  },

  _stopPopupTimer: function () {
    // Cancels the popup timer because the user has stop hovering on the MenuItem, etc.
    if (this.hover_timer) {
      this.hover_timer = this.hover_timer.remove();
    }
  },

  _stopPendingCloseTimer: function () {
    // Cancels the pending-close timer because the close has been preempted
    if (this._pendingClose_timer) {
      this._pendingClose_timer = this._pendingClose_timer.remove();
    }
  },

  _getTopMenu: function () {
    // Returns the top menu in this chain of Menus
    for (var top = this; top.parentMenu; top = top.parentMenu) {}
    return top;
  },

  onItemClick: function (/*dijit/_WidgetBase*/ item: any, /*Event*/ evt: any) {
    // Handle clicks on an item.

    if (this.passive_hover_timer) {
      this.passive_hover_timer.remove();
    }

    this.focusChild(item);

    if (item.disabled) {
      return false;
    }

    if (item.popup) {
      this.set('selected', item);
      this.set('activated', true);
      var byKeyboard = /^key/.test(evt._origType || evt.type) ||
        (evt.clientX == 0 && evt.clientY == 0);	// detects accessKey like ALT+SHIFT+F, where type is "click"
      this._openItemPopup(item, byKeyboard);
    } else {
      // before calling user defined handler, close hierarchy of menus
      // and restore focus to place it was when menu was opened
      this.onExecute();

      // user defined handler for click
      item._onClick ? item._onClick(evt) : item.onClick(evt);
    }
  },

  _openItemPopup: function (/*dijit/MenuItem*/ from_item: any, /*Boolean*/ focus?: any) {
    // Open the popup to the side of/underneath the current menu item, and optionally focus first item

    if (from_item == this.currentPopupItem) {
      // Specified popup is already being shown, so just return
      return;
    }
    if (this.currentPopupItem) {
      // If another popup is currently shown, then close it
      this._stopPendingCloseTimer();
      this.currentPopupItem._closePopup();
    }
    this._stopPopupTimer();

    var popup = from_item.popup;
    popup.parentMenu = this;

    // detect mouseover of the popup to handle lazy mouse movements that temporarily focus other menu items
    this.own(this._mouseoverHandle = once(popup.domNode, 'mouseover', lang.hitch(this, '_onPopupHover')));

    var self = this;
    from_item._openPopup({
      parent: this,
      orient: this._orient || ['after', 'before'],
      onCancel: function () { // called when the child menu is canceled
        if (focus) {
          // put focus back on my node before focused node is hidden
          self.focusChild(from_item);
        }

        // close the submenu (be sure this is done _after_ focus is moved)
        self._cleanUp();
      },
      onExecute: lang.hitch(this, '_cleanUp', true),
      onClose: function () {
        // Remove handler created by onItemHover
        if (self._mouseoverHandle) {
          self._mouseoverHandle.remove();
          delete self._mouseoverHandle;
        }
      },
    }, focus);

    this.currentPopupItem = from_item;
  },

  onOpen: function (/*Event*/ /*===== e =====*/) {
    // Callback when this menu is opened.  Called by the popup manager.
    this.isShowingNow = true;
    this.set('activated', true);
  },

  onClose: function () {
    // Callback when this menu is closed.  Called by the popup manager.
    this.set('activated', false);
    this.set('selected', null);
    this.isShowingNow = false;
    this.parentMenu = null;
  },

  _closeChild: function () {
    // Called when submenu is clicked or focus is lost.  Close hierarchy of menus.
    this._stopPopupTimer();

    if (this.currentPopupItem) {
      // If focus is on a descendant MenuItem then move focus to me, because IE doesn't
      // like it when you display:none a node with focus, and also so keyboard users
      // don't lose control.
      if (this.focused) {
        domAttr.set(this.selected.focusNode, 'tabIndex', this.tabIndex);
        this.selected.focusNode.focus();
      }

      // Close all popups that are open and descendants of this menu
      this.currentPopupItem._closePopup();
      this.currentPopupItem = null;
    }
  },

  _onItemFocus: function (/*MenuItem*/ item: any) {
    // Called when child of this Menu gets focus from: 1) clicking it, 2) tabbing into
    // it, 3) being opened by a parent menu.  Not called just from mouse hover.

    if (this._hoveredChild && this._hoveredChild != item) {
      this.onItemUnhover(this._hoveredChild);	// any previous mouse movement is trumped by focus selection
    }
    this.set('selected', item);
  },

  _onBlur: function () {
    // Called when focus is moved away from this Menu and it's submenus.
    this._cleanUp(true);
    this.inherited(arguments);
  },

  _cleanUp: function (/*Boolean*/ clearSelectedItem?: any) {
    // Called when the user is done with this menu.  Closes hierarchy of menus.
    this._closeChild(); // don't call this.onClose since that's incorrect for MenuBar's that never close
    if (typeof this.isShowingNow == 'undefined') { // non-popup menu doesn't call onClose
      this.set('activated', false);
    }

    if (clearSelectedItem) {
      this.set('selected', null);
    }
  },
});
